import LogoIcon from '../../../assets/LOGO.svg';
import { MouseEventHandler, ReactNode, useState } from 'react';
import { Popover } from 'antd';
import Server from '../../atoms/Icon/Server';
import { css } from '@emotion/react';

export interface NavBarProps {
    'data-test'?: string;
    'data-test-id'?: string;
    customerImage?: any;
    userInitials: string;
    logoutClickHandler: MouseEventHandler;
    children?: ReactNode;
    refreshLogsData?: any[];
    redirect?: any;
    openAskAsper?: any;
    isAskAsperAllowed?: boolean;
    onDataLogHover?: () => void;
    onProfileClick?: () => void;
}

const ringStyle = css`
    width: 20px;
    height: 20px;
    border: 4px solid #ffffff;
    border-radius: 50%;
    background-color: transparent;
`;

const NavBar: React.FC<NavBarProps> = (props) => {
    const {
        children,
        userInitials,
        logoutClickHandler,
        customerImage = LogoIcon,
        refreshLogsData,
        redirect,
        openAskAsper,
        isAskAsperAllowed,
        onDataLogHover = () => {},
        onProfileClick = () => {},
    } = props;
    const [isDataLogOpen, setIsDataLogOpen] = useState(false);
    return (
        <div
            className="w-full h-[3.875rem] px-4 py-[0.8125rem] flex items-center bg-white shadow-md gap-[3rem]"
            data-test={props['data-test']}
            data-test-id={props['data-test-id']}
            data-asper-navbar
        >
            <div className="flex items-center gap-[1.5rem] basis-5/6 flex-shrink">
                <img
                    src={customerImage}
                    alt="Customer image"
                    className="w-[5.313rem] min-w-16 align-middle cursor-pointer"
                    onClick={() => redirect?.()}
                    data-test-id="customer-logo"
                />
                <div className="flex items-center gap-4" data-test-id="navbar-children">
                    {children}
                </div>
            </div>
            <div className="flex items-center gap-4 ml-auto">
                {isAskAsperAllowed && (
                    <div
                        style={{ boxShadow: 'rgba(0, 0, 0, 0.1) 0px 0px 0px 0px, rgba(0, 0, 0, 0.1) 0px 2px 0px 0px' }}
                        className="flex flex-row justify-center items-center text-white bg-gradient-to-r from-pink-500 via-purple-700 to-blue-700 w-[112px] h-[32px] gap-[8px] rounded-[6px] opacity-100 text-[14px] cursor-pointer"
                        onClick={() => openAskAsper?.()}
                        data-test-id="ask-asper-button"
                    >
                        <div css={ringStyle} data-test-id="ask-asper-ring"></div>
                        AskAsper
                    </div>
                )}
                <div
                    onMouseEnter={() => {
                        onDataLogHover();
                        setIsDataLogOpen(true);
                    }}
                    onMouseLeave={() => setIsDataLogOpen(false)}
                    data-test-id="data-log-section"
                >
                    <Popover
                        trigger={['hover']}
                        className="cursor-pointer"
                        placement="bottom"
                        open={isDataLogOpen}
                        content={
                            <div className="cursor-pointer font-sans" data-test-id="data-log-popover">
                                <div className="mb-1 font-semibold">Data Refresh Status</div>
                                <div className="text-xs leading-5">
                                    <div className="grid grid-cols-4 font-bold pb-1">
                                        <p>Data</p>
                                        <p>Updated On</p>
                                        <p>Available From</p>
                                        <p>Available Till</p>
                                    </div>
                                    {refreshLogsData?.map(({ dataType, updatedOn, availableFrom, availableTill }) => (
                                        <div
                                            className="grid grid-cols-4 gap-4 py-1"
                                            data-test-id="data-log-item"
                                        >
                                            <p>{dataType}:</p>
                                            <p>{updatedOn}</p>
                                            <p>{availableFrom}</p>
                                            <p>{availableTill}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        }
                    >
                        <Server color="#44338A" size={24} data-test-id="data-log-icon" />
                    </Popover>
                </div>
                <div
                    className="w-8 h-8 bg-[#F7F5FF] rounded-full flex items-center justify-center"
                    onClick={onProfileClick}
                    data-test-id="profile-section"
                >
                    <Popover
                        trigger={['click']}
                        className="cursor-pointer"
                        placement="bottom"
                        content={
                            <div className="cursor-pointer" onClick={logoutClickHandler} data-test-id="logout-button">
                                Log Out
                            </div>
                        }
                    >
                        <span
                            className="text-brand font-sans capitalize font-bold text-[0.6875rem]"
                            data-test-id="user-initials"
                        >
                            {userInitials}
                        </span>
                    </Popover>
                </div>
            </div>
        </div>
    );
};

export default NavBar;
